import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ClubCrest } from '../components/ClubCrest'
import { appearanceProbability } from '../game'
import { buildMatchSimulation } from './simulation'
import type { LeagueFixtureOutcome, MatchExpectation, MatchOptions, MatchSummary, MatchSimulation } from './types'

interface LeagueBlockTickerProps {
  options: MatchOptions
  onComplete: (summary: MatchSummary) => void
}

const MAX_FIXTURE_COUNT = 5
const DEFAULT_BLOCK_DURATION_MS = 7_000
const POINTS_PER_WIN = 3
const POINTS_PER_DRAW = 1
const POINTS_PER_LOSS = 0

function pointsFor(outcome: LeagueFixtureOutcome): number {
  return outcome === 'win' ? POINTS_PER_WIN : outcome === 'draw' ? POINTS_PER_DRAW : POINTS_PER_LOSS
}

function rotate<T>(items: readonly T[], offset: number): T[] {
  return items.map((_, index) => items[(index + offset) % items.length]!)
}

function seededRandom(seed: number): () => number {
  let value = seed >>> 0
  return () => {
    value += 0x6D2B79F5
    let result = value
    result = Math.imul(result ^ result >>> 15, result | 1)
    result ^= result + Math.imul(result ^ result >>> 7, result | 61)
    return ((result ^ result >>> 14) >>> 0) / 4294967296
  }
}

function textSeed(value: string): number {
  return [...value].reduce((total, character) => Math.imul(total ^ character.charCodeAt(0), 16777619) >>> 0, 2166136261)
}

export { appearanceProbability }

export function playerMinutesForFixture(reputation: number, fixtureId: string): number {
  const random = seededRandom(textSeed(`${fixtureId}:minutes`))
  if (random() >= appearanceProbability(reputation)) return 0
  const starterChance = Math.max(.15, Math.min(.9, .15 + Math.max(0, Math.min(100, reputation)) * .0075))
  return random() < starterChance ? 60 + Math.floor(random() * 31) : 10 + Math.floor(random() * 36)
}

interface LeagueBlockPerformance {
  fixtureCount?: number
  appearances: number
  minutes: number
  goals: number
  assists: number
  playerPoints: number
}

export function leagueBlockOvrGain(performance: LeagueBlockPerformance): number {
  if (performance.appearances <= 0 || performance.minutes <= 0) return 0
  const fixtureCount = Math.max(1, Math.floor(performance.fixtureCount ?? 3))
  const scale = fixtureCount / 3
  const contributions = Math.max(0, performance.goals) + Math.max(0, performance.assists)
  if (
    performance.minutes >= Math.ceil(180 * scale)
    && contributions >= Math.ceil(2 * scale)
    && performance.playerPoints >= Math.ceil(7 * scale)
  ) return 3
  if (performance.minutes >= Math.ceil(90 * scale) && contributions >= Math.ceil(scale)) return 2
  return 1
}

export function leagueBlockOutcomes(winProbability: number, matchId: string, count: number, seed?: number, allowDraws = true): LeagueFixtureOutcome[] {
  const probability = Math.max(0, Math.min(1, winProbability))
  const random = seededRandom(textSeed(`${matchId}:${seed ?? ''}:league-block`))
  const drawProbability = allowDraws ? (1 - probability) * .28 : 0
  const outcomes: LeagueFixtureOutcome[] = Array.from({ length: count }, () => {
    const roll = random()
    if (roll < probability) return 'win'
    if (roll < probability + drawProbability) return 'draw'
    return 'loss'
  })
  return rotate(outcomes, textSeed(`${matchId}:order`) % Math.max(1, outcomes.length))
}

type LeagueFixturePerformance = Pick<MatchSimulation, 'outcome' | 'playerGoals' | 'playerAssists' | 'playerMinutes'>

export function leagueBlockReputationGain(fixtures: readonly LeagueFixturePerformance[]): number {
  const played = fixtures.filter((fixture) => (fixture.playerMinutes ?? 0) > 0)
  if (!played.length) return -2
  const contributions = played.reduce((total, fixture) => total + fixture.playerGoals + fixture.playerAssists, 0)
  const playerPoints = played.reduce((total, fixture) => total + pointsFor(fixture.outcome), 0)
  const blockScale = Math.min(1, 3 / fixtures.length)
  const score = Math.round(
    (played.length + contributions * 2 + playerPoints / 3) * blockScale
    - (playerPoints === 0 ? 1 : 0),
  )
  return Math.min(8, Math.max(0, score))
}

export function selectLeagueBlockOpponents(options: MatchOptions): string[] {
  const pool = [...new Set(options.opponents?.length ? options.opponents : [options.opponent])]
    .filter((name) => name && name !== options.homeTeam)
  if (!pool.length) throw new Error(`No quedan rivales disponibles para ${options.homeTeam}.`)
  const previous = new Set(options.previousOpponents ?? [])
  const random = seededRandom(textSeed(`${options.matchId}:${options.seed ?? ''}`))
  const requestedCount = Math.max(1, Math.floor(options.fixtureCount ?? MAX_FIXTURE_COUNT))
  const selected: string[] = []

  while (pool.length && selected.length < Math.min(MAX_FIXTURE_COUNT, requestedCount)) {
    const weights = pool.map((name) => previous.has(name) ? 1 : 5)
    const totalWeight = weights.reduce((total, weight) => total + weight, 0)
    let roll = random() * totalWeight
    let selectedIndex = weights.length - 1
    for (let index = 0; index < weights.length; index += 1) {
      roll -= weights[index]!
      if (roll < 0) { selectedIndex = index; break }
    }
    selected.push(pool[selectedIndex]!)
    pool.splice(selectedIndex, 1)
  }

  return selected
}

export function LeagueBlockTicker({ options, onComplete }: LeagueBlockTickerProps) {
  const fingerprint = `${options.matchId}-${options.expectedResult}-${(options.opponents ?? []).join('|')}-${(options.previousOpponents ?? []).join('|')}-${options.assistBonusPercentagePoints ?? 0}-${options.clubReputation ?? 45}-${options.careerStage ?? 3}-${options.playerOverall ?? 'baseline'}-${options.player.position}`
  const fixtures = useMemo(() => {
    const opponents = selectLeagueBlockOpponents(options)
    const fallbackProbability = options.expectedResult === 'win' ? .6 : .4
    const outcomes = leagueBlockOutcomes(options.winProbability ?? fallbackProbability, options.matchId, opponents.length, options.seed, true)
    return opponents.map((opponent, index): MatchSimulation => {
      const matchId = `${options.matchId}-partido-${index + 1}`
      return buildMatchSimulation({
        ...options,
        format: 'single',
        matchId,
        title: 'Partido del tramo',
        opponent,
        expectedResult: outcomes[index]!,
        playerMinutes: playerMinutesForFixture(options.clubReputation ?? 45, matchId),
      })
    })
  }, [fingerprint])
  const durationMs = options.durationMs ?? DEFAULT_BLOCK_DURATION_MS
  const [completed, setCompleted] = useState(0)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [paused, setPaused] = useState(false)
  const timerRef = useRef<number | null>(null)
  const elapsedRef = useRef(0)
  const pausedRef = useRef(false)

  useEffect(() => {
    setCompleted(0); setElapsedMs(0); setPaused(false)
    elapsedRef.current = 0; pausedRef.current = false
    let previousTick = performance.now()
    timerRef.current = window.setInterval(() => {
      const now = performance.now()
      if (!pausedRef.current) elapsedRef.current += now - previousTick
      previousTick = now
      if (pausedRef.current) return
      setElapsedMs(elapsedRef.current)
      const nextCompleted = Math.min(fixtures.length, Math.floor(elapsedRef.current / durationMs * fixtures.length))
      setCompleted(nextCompleted)
      if (nextCompleted >= fixtures.length && timerRef.current !== null) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
    }, 100)
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [fixtures, durationMs])

  const visibleFixtures = fixtures.slice(0, completed)
  const fixtureCount = fixtures.length
  const finished = completed === fixtures.length
  const fixtureDurationMs = durationMs / fixtureCount
  const activeFixtureIndex = finished ? null : completed
  const activeMinute = activeFixtureIndex === null
    ? 90
    : Math.min(90, Math.floor(Math.max(0, elapsedMs - activeFixtureIndex * fixtureDurationMs) / fixtureDurationMs * 90))
  const targetPoints = Math.ceil(fixtureCount * 1.5)
  const points = visibleFixtures.reduce((total, fixture) => total + pointsFor(fixture.outcome), 0)
  const totalBlockWins = fixtures.filter((fixture) => fixture.outcome === 'win').length
  const totalBlockDraws = fixtures.filter((fixture) => fixture.outcome === 'draw').length
  const blockAppearances = fixtures.filter((fixture) => (fixture.playerMinutes ?? 0) > 0).length
  const blockMinutes = fixtures.map((fixture) => fixture.playerMinutes ?? 0)
  const totalBlockPoints = fixtures.reduce((total, fixture) => total + pointsFor(fixture.outcome), 0)
  const blockOutcome: MatchExpectation = totalBlockPoints >= targetPoints ? 'win' : 'loss'
  const totalPlayerGoals = fixtures.reduce((total, fixture) => total + fixture.playerGoals, 0)
  const totalPlayerAssists = fixtures.reduce((total, fixture) => total + fixture.playerAssists, 0)
  const totalPlayerMinutes = blockMinutes.reduce((total, minutes) => total + minutes, 0)
  const totalPlayerPoints = fixtures.reduce((total, fixture) => (fixture.playerMinutes ?? 0) > 0 ? total + pointsFor(fixture.outcome) : total, 0)
  const blockOvrGain = leagueBlockOvrGain({ fixtureCount, appearances: blockAppearances, minutes: totalPlayerMinutes, goals: totalPlayerGoals, assists: totalPlayerAssists, playerPoints: totalPlayerPoints })
  const reputationGain = leagueBlockReputationGain(fixtures)
  const blockPlayerWins = fixtures.filter((fixture) => fixture.outcome === 'win' && (fixture.playerMinutes ?? 0) > 0).length
  const summary: MatchSummary = {
    matchId: options.matchId,
    opponent: options.opponent,
    homeGoals: fixtures.reduce((total, fixture) => total + fixture.homeGoals, 0),
    awayGoals: fixtures.reduce((total, fixture) => total + fixture.awayGoals, 0),
    playerGoals: totalPlayerGoals,
    playerAssists: totalPlayerAssists,
    outcome: blockOutcome,
    blockPoints: totalBlockPoints,
    blockWins: totalBlockWins,
    blockDraws: totalBlockDraws,
    blockPlayerWins,
    blockOutcomes: fixtures.map((fixture) => fixture.outcome),
    blockFixtures: fixtures.map((fixture) => ({ ...fixture, playerMinutes: fixture.playerMinutes ?? 0 })),
    blockOvrGain,
    blockAppearances,
    blockMinutes,
    reputationGain,
    fixturesPlayed: fixtures.length,
    blockOpponents: fixtures.map((fixture) => fixture.opponent),
    events: fixtures.flatMap((fixture) => fixture.events),
  }

  const togglePause = () => setPaused((current) => {
    pausedRef.current = !current
    return !current
  })

  const simulateResult = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = null
    elapsedRef.current = durationMs
    setElapsedMs(durationMs)
    pausedRef.current = false
    setPaused(false)
    setCompleted(fixtures.length)
  }

  return (
    <section className="match-ticker league-block" aria-label={`${options.title}: bloque de ${fixtureCount} partidos de ${options.homeTeam}`}>
      <header className="match-ticker__header">
        <div><p className="eyebrow">{options.title}</p><h2>{`${fixtureCount === 3 ? 'Tres' : fixtureCount} partidos. Cada punto cuenta.`}</h2></div>
        <span className={`match-ticker__status${finished ? ' is-final' : ''}`}>{finished ? 'Final' : paused ? 'Pausa' : `${completed}/${fixtureCount}`}</span>
      </header>

      <div className="league-block__summary" aria-live="polite">
        <div className="league-block__club"><ClubCrest name={options.homeTeam} size="large" /><div><small>Tu equipo</small><strong title={options.homeTeam}>{options.homeTeam}</strong></div></div>
        <div className="league-block__points"><strong key={points}>{points} <i>/</i> {fixtureCount * POINTS_PER_WIN}</strong><span>puntos de liga</span></div>
      </div>

      <p className="league-block__selection"><span>Prestigio en el club · {options.clubReputation ?? 45}/100</span><strong>{Math.round(appearanceProbability(options.clubReputation ?? 45) * 100)}% de probabilidad de jugar cada partido</strong></p>

      <dl className="league-block__player-stats" aria-label="Tu rendimiento en el tramo">
        <div><dt>Partidos</dt><dd>{visibleFixtures.filter((fixture) => (fixture.playerMinutes ?? 0) > 0).length}/{fixtureCount}</dd></div>
        <div><dt>Minutos</dt><dd>{visibleFixtures.reduce((total, fixture) => total + (fixture.playerMinutes ?? 0), 0)}</dd></div>
        <div><dt>Goles</dt><dd>{visibleFixtures.reduce((total, fixture) => total + fixture.playerGoals, 0)}</dd></div>
        <div><dt>Asistencias</dt><dd>{visibleFixtures.reduce((total, fixture) => total + fixture.playerAssists, 0)}</dd></div>
        {finished && <div className="league-block__progression"><dt>Progresión</dt><dd>+{blockOvrGain} OVR · {reputationGain >= 0 ? '+' : ''}{reputationGain} prestigio</dd></div>}
      </dl>

      <div className="match-ticker__time" role="progressbar" aria-label="Partidos simulados" aria-valuemin={0} aria-valuemax={fixtureCount} aria-valuenow={completed} aria-valuetext={`${completed} de ${fixtureCount} partidos simulados${paused ? ', simulación pausada' : ''}`}><span style={{ transform: `scaleX(${completed / fixtureCount})` }} /></div>

      <section className="league-fixtures" aria-labelledby="league-fixtures-title">
        <header><h3 id="league-fixtures-title">Resultados del tramo</h3><span>{finished ? 'Bloque terminado' : 'Simulando ×2,25'}</span></header>
        <ol>
          {fixtures.map((fixture, index) => {
            const isComplete = index < completed
            const isLive = index === completed && !finished
            const resultLabel = fixture.outcome === 'win' ? 'Victoria' : fixture.outcome === 'draw' ? 'Empate' : 'Derrota'
            const liveScore = fixture.events.reduce((score, event) => {
              if (event.type !== 'goal' || event.minute > activeMinute) return score
              return event.team === 'home'
                ? { ...score, home: score.home + 1 }
                : { ...score, away: score.away + 1 }
            }, { home: 0, away: 0 })
            const livePlayerGoals = fixture.events.filter((event) => event.type === 'goal' && event.minute <= activeMinute && event.actor === options.player.name).length
            const livePlayerAssists = fixture.events.filter((event) => event.type === 'goal' && event.minute <= activeMinute && event.assist === options.player.name).length
            return <li
              key={fixture.matchId}
              className={`${isComplete ? `is-complete is-${fixture.outcome}` : ''}${isLive ? ' is-live' : ''}`}
              aria-label={isComplete ? `${fixture.opponent}: ${resultLabel}, ${fixture.homeGoals} a ${fixture.awayGoals}; ${(fixture.playerMinutes ?? 0) > 0 ? `${fixture.playerMinutes} minutos, ${fixture.playerGoals} goles y ${fixture.playerAssists} asistencias` : 'no jugó'}` : undefined}
            >
              <ClubCrest name={fixture.opponent} size="small" />
              <strong title={fixture.opponent}>{fixture.opponent}</strong>
              <span className={`league-fixtures__minutes${isComplete && !(fixture.playerMinutes ?? 0) ? ' did-not-play' : ''}`}>{isComplete ? ((fixture.playerMinutes ?? 0) > 0 ? `${fixture.playerMinutes}'` : 'No jugó') : '—'}</span>
              <span className="league-fixtures__production">{isComplete ? ((fixture.playerMinutes ?? 0) > 0 ? `${fixture.playerGoals} G · ${fixture.playerAssists} A` : 'No jugó') : isLive ? `${livePlayerGoals} G · ${livePlayerAssists} A` : '—'}</span>
              <span className="league-fixtures__outcome">{isComplete ? resultLabel : ''}</span>
              <span
                className="league-fixtures__score"
                aria-label={isLive ? `Simulando: ${liveScore.home} a ${liveScore.away} en el minuto ${activeMinute}` : undefined}
              >{isComplete ? `${fixture.homeGoals} — ${fixture.awayGoals}` : isLive ? <><i /> {liveScore.home} — {liveScore.away} · {activeMinute}'</> : 'Pendiente'}</span>
            </li>
          })}
        </ol>
      </section>

      <footer className="match-ticker__footer match-ticker__footer--controls-only">
        {finished ? <button className="button button--primary" type="button" onClick={() => onComplete(summary)}>Continuar</button> : <div className="match-ticker__controls"><button className="button match-ticker__pause" type="button" aria-pressed={paused} onClick={togglePause}><Icon icon={paused ? 'mdi:play' : 'mdi:pause'} /> {paused ? 'Reanudar' : 'Pausar'}</button><button className="button button--primary" type="button" onClick={simulateResult}><Icon icon="mdi:skip-forward" /> Simular hasta el resultado</button></div>}
      </footer>
    </section>
  )
}
